// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// components/security/ui.tsx - the small building blocks every Security screen composes (UI principles §10: compose the
// locked controls, never invent new ones): icons (the prototype's IC set), number field, on/off toggle, pick-one pills,
// modal shell, destructive/typed confirm (§23), popover menu (§22), and text helpers.
// Modals, drawers and menus portal to <body> and carry .vs-sec so they are styled the same over any page.
import { CSSProperties, ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../pages/settings-controls.css';
import '../../styles/security.css';

// ── icons ──────────────────────────────────────────────────────────────────────
export const IC: Record<string, ReactNode> = {
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  shieldOk: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 12 0v1" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
  pen: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
  key: <><circle cx="7.5" cy="15.5" r="4.5" /><path d="M21 2l-9.6 9.6M15.5 7.5l3 3L22 7l-3-3" /></>,
  finger: <><path d="M12 11v4M7.5 8.5A5 5 0 0 1 17 11v2a9 9 0 0 1-1 4M6.5 13a13 13 0 0 0 1.4 6M9.5 11a2.5 2.5 0 0 1 5 0v2a13 13 0 0 1-1.4 6" /><path d="M4.5 10a7.5 7.5 0 0 1 14.2-3.3" /></>,
  usb: <><rect x="7" y="2" width="10" height="9" rx="1" /><path d="M5 11h14v6a5 5 0 0 1-5 5h-4a5 5 0 0 1-5-5z" /><path d="M10 6h.01M14 6h.01" /></>,
  app: <><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M11 18h2" /></>,
  sms: <><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.8L3 21l1.9-5a8.4 8.4 0 0 1-.8-3.6 8.5 8.5 0 1 1 16.9.1z" /><path d="M8 11h.01M12 11h.01M16 11h.01" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" /></>,
  codes: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9h4M13 9h4M7 13h4M13 13h4M7 17h4" /></>,
  kebab: <><circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></>,
  x: <path d="M18 6L6 18M6 6l12 12" />,
  check: <path d="M20 6L9 17l-5-5" />,
  chev: <path d="M9 18l6-6-6-6" />,
  chevDown: <path d="M6 9l6 6 6-6" />,
  chevUp: <path d="M6 15l6-6 6 6" />,
  back: <path d="M15 18l-6-6 6-6" />,
  up: <path d="M12 19V5M5 12l7-7 7 7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
  send: <><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></>,
  reset: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></>,
  out: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></>,
  ban: <><circle cx="12" cy="12" r="10" /><path d="M4.9 4.9l14.2 14.2" /></>,
  trash: <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />,
  warn: <><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></>,
  info: <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></>,
  phone: <><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M11 18h2" /></>,
  download: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  badge: <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" /><path d="m9 12 2 2 4-4" /></>,
  puzzle: <path d="M19.44 7.85c-.05.32.06.65.29.88l1.57 1.57c.47.47.7 1.09.7 1.7s-.23 1.24-.7 1.71l-1.61 1.61a.98.98 0 0 1-.84.28c-.47-.07-.8-.48-.97-.93a2.5 2.5 0 1 0-3.21 3.21c.45.17.86.5.93.97a.98.98 0 0 1-.28.84l-1.61 1.61c-.47.47-1.09.7-1.7.7s-1.24-.23-1.71-.7l-1.57-1.57a1.03 1.03 0 0 0-.88-.29c-.49.07-.84.5-1.02.97a2.5 2.5 0 1 1-3.24-3.24c.46-.18.9-.53.97-1.02a1.03 1.03 0 0 0-.29-.88l-1.57-1.57A2.4 2.4 0 0 1 2 12c0-.62.24-1.23.7-1.7l1.53-1.53c.24-.24.58-.35.92-.3.51.08.88.53 1.07 1.01a2.5 2.5 0 1 0 3.26-3.26c-.48-.2-.93-.56-1.01-1.07-.05-.34.06-.68.3-.92L10.3 2.7A2.4 2.4 0 0 1 12 2c.62 0 1.23.24 1.7.7l1.57 1.57c.23.23.56.34.88.29.49-.07.84-.5 1.02-.97a2.5 2.5 0 1 1 3.24 3.24c-.46.18-.9.53-.97 1.02Z" />,
  unlock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 7.5-2" /></>,
};

export function Svg({ d, className, style }: { d: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} style={style} aria-hidden="true">
      {d}
    </svg>
  );
}
export const Ic = ({ k, className }: { k: keyof typeof IC | string; className?: string }) => <Svg d={IC[k]} className={className} />;

// ── text helpers ───────────────────────────────────────────────────────────────
export function plural(n: number, one: string, many?: string): string { return `${n} ${n === 1 ? one : (many ?? `${one}s`)}`; }
export function digits(s: string | null | undefined): string { return String(s ?? '').replace(/\D/g, ''); }
export function initials(first?: string | null, last?: string | null, fallback?: string | null): string {
  const f = (first ?? '').trim(), l = (last ?? '').trim();
  if (f || l) return ((f[0] ?? '') + (l[0] ?? '')).toUpperCase() || '?';
  // v12.10.9.4 (#1339 F5): no name -> ONE letter (the first of the email / mobile), never two.
  return (String(fallback ?? '').trim().replace(/^\+/, '')[0] ?? '?').toUpperCase();
}
export function fmtDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', opts);
}
export function fmtLong(iso: string | null | undefined): string { return fmtDate(iso, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }
export function fmtClock(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
/** "Today, 9:14 AM" / "Yesterday, 5:41 PM" / "Sep 21, 2026" */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - day.getTime()) / 86400000);
  if (diff === 0) return `Today, ${fmtClock(iso)}`;
  if (diff === 1) return `Yesterday, ${fmtClock(iso)}`;
  return fmtDate(iso);
}
export function lastFour(mobile: string | null | undefined): string { return digits(mobile).slice(-4); }

// ── number field (§4) ──────────────────────────────────────────────────────────
export function NumField({ value, onChange, min, max, step = 1, unit, narrow = true, disabled, label }: {
  value: number; onChange: (v: number) => void; min: number; max: number; step?: number; unit?: string; narrow?: boolean; disabled?: boolean; label?: string;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const bump = (dir: number) => { const v = clamp((Number(value) || 0) + dir * step); setText(String(v)); onChange(v); };
  return (
    <div className={`numfield${narrow ? ' sc-nf-narrow' : ''}${disabled ? ' sc-nf-dis' : ''}`}>
      <input type="text" inputMode="numeric" className="nf-input" value={text} disabled={disabled} aria-label={label}
        onChange={(e) => {
          const t = e.target.value.replace(/[^0-9]/g, '');
          setText(t);
          if (t !== '') { const n = Number(t); if (n >= min && n <= max) onChange(n); }
        }}
        onBlur={() => { let n = Number(text); if (text === '' || Number.isNaN(n)) n = min; n = clamp(n); setText(String(n)); if (n !== value) onChange(n); }} />
      {unit && <div className="nf-labels"><span className="nf-unit">{unit}</span></div>}
      <div className="nf-steppers">
        <button type="button" className="nf-up" disabled={disabled} onClick={() => bump(1)} aria-label="Increase" tabIndex={-1}><Ic k="chevUp" /></button>
        <button type="button" className="nf-down" disabled={disabled} onClick={() => bump(-1)} aria-label="Decrease" tabIndex={-1}><Ic k="chevDown" /></button>
      </div>
    </div>
  );
}

// ── on/off toggle (§3) ─────────────────────────────────────────────────────────
export function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" className={`day-toggle${on ? '' : ' off'}`} role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}>
      <span className="knob" />
    </button>
  );
}

// ── pick-one pills (§2) ────────────────────────────────────────────────────────
export function Seg<T extends string | number>({ value, options, onChange, disabled, label }: {
  value: T; options: [T, ReactNode][]; onChange: (v: T) => void; disabled?: boolean; label?: string;
}) {
  return (
    <div className="seg-group" role="radiogroup" aria-label={label}>
      {options.map(([v, l]) => {
        const on = v === value;
        return (
          <button key={String(v)} type="button" className={`seg${on ? ' on' : ''}`} role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(v)}>
            <span className="seg-dot" />{l}
          </button>
        );
      })}
    </div>
  );
}

// ── modal shell ────────────────────────────────────────────────────────────────
// One Esc stack for every Security overlay: Esc closes only the top one.
const escStack: number[] = [];
let escSeq = 0;
export function useEscClose(onClose: (() => void) | undefined, active = true) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!active) return;
    const id = ++escSeq;
    escStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || escStack[escStack.length - 1] !== id) return;
      e.stopPropagation();
      ref.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = escStack.indexOf(id);
      if (i >= 0) escStack.splice(i, 1);
    };
  }, [active]);
}

export function ScModal({ children, onClose, wide, className, label, closeOnScrim = true, top }: {
  children: ReactNode; onClose: () => void; wide?: boolean; className?: string; label: string; closeOnScrim?: boolean; top?: boolean;
}) {
  useEscClose(onClose);
  return createPortal(
    <div className={`vs-sec sc-scrim${top ? ' top' : ''}`} onMouseDown={(e) => { if (closeOnScrim && e.target === e.currentTarget) onClose(); }}>
      <div className={`sc-modal${wide ? ' wide' : ''}${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={label}>
        <button type="button" className="modal-x" onClick={onClose} aria-label="Close"><Ic k="x" /></button>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ── confirm dialog: plain (M3) / destructive (M1) / typed (M2) - §23 ─────────────
export interface ConfirmOpts {
  sev?: 'bad' | 'warn' | 'info';
  title: string;
  body: ReactNode;
  label: string;
  danger?: boolean;
  /** first + last name the person must type (never shown as the placeholder) */
  typed?: string;
  cancelLabel?: string;
  noCancel?: boolean;
  /** runs on OK; may throw - the message is shown inline and the dialog stays open */
  onOk: () => Promise<void> | void;
  errorText?: (err: unknown) => string | null;
}
export function ConfirmDialog({ opts, onClose }: { opts: ConfirmOpts; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const okRef = useRef<HTMLButtonElement>(null);
  const typedRef = useRef<HTMLInputElement>(null);
  const typedOk = !opts.typed || typed.trim().toLowerCase() === opts.typed.trim().toLowerCase();
  useEffect(() => { (opts.typed ? typedRef.current : okRef.current)?.focus({ preventScroll: true }); }, [opts.typed]);
  const sevIc = opts.sev === 'bad' ? 'trash' : opts.sev === 'warn' ? 'warn' : 'info';
  async function ok() {
    if (!typedOk || busy) return;
    setBusy(true); setErr('');
    try { await opts.onOk(); onClose(); }
    catch (e) {
      const msg = opts.errorText ? opts.errorText(e) : 'That didn’t work. Try again.';
      if (msg === null) { onClose(); return; }   // e.g. Confirm-it's-you cancelled: close quietly
      setErr(msg);
    } finally { setBusy(false); }
  }
  return (
    <ScModal onClose={onClose} label={opts.title} closeOnScrim={!opts.typed}>
      <span className={`sev ${opts.sev ?? 'info'}`}><Ic k={sevIc} /></span>
      <h4>{opts.title}</h4>
      <p>{opts.body}</p>
      {opts.typed && (
        <div className="mrow">
          <label htmlFor="scTyped">Type their first and last name to confirm</label>
          <div className={`sc-typed${typedOk && typed ? ' ok' : ''}`}>
            <input id="scTyped" ref={typedRef} autoComplete="off" placeholder="First and last name" value={typed} onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ok(); }} />
            <span className="ok-ic"><Ic k="check" /></span>
          </div>
        </div>
      )}
      {err && <div className="sc-warn bad" style={{ marginBottom: 12 }}><Ic k="warn" /><div>{err}</div></div>}
      <div className="mact">
        {!opts.noCancel && <button type="button" className="mc" onClick={onClose}>{opts.cancelLabel ?? 'Cancel'}</button>}
        <button type="button" ref={okRef} className={opts.danger ? 'sc-danger solid' : 'ma'} disabled={!typedOk || busy} onClick={ok}>
          {busy ? 'Working…' : opts.label}
        </button>
      </div>
    </ScModal>
  );
}

/** Hook: const [confirmEl, ask] = useConfirm(); ask({...}); render {confirmEl}. */
export function useConfirm(): [ReactNode, (o: ConfirmOpts) => void] {
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const ask = useCallback((o: ConfirmOpts) => setOpts(o), []);
  const el = opts ? <ConfirmDialog opts={opts} onClose={() => setOpts(null)} /> : null;
  return [el, ask];
}

// ── popover menu (§22) ─────────────────────────────────────────────────────────
export type MenuItem = 'sep' | { icon?: string; label: string; onClick: () => void; danger?: boolean; disabled?: boolean; title?: string };
export function PopMenu({ anchor, items, onClose, label }: { anchor: HTMLElement; items: MenuItem[]; onClose: () => void; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEscClose(onClose);
  useLayoutEffect(() => {
    const m = ref.current; if (!m) return;
    const r = anchor.getBoundingClientRect();
    const w = m.offsetWidth, h = m.offsetHeight;
    let left = r.right - w; if (left < 8) left = r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    let top = r.bottom + 6; if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 6);
    setPos({ left, top });
    const first = m.querySelector('button:not(:disabled)') as HTMLButtonElement | null;
    first?.focus({ preventScroll: true });
  }, [anchor]);
  useEffect(() => {
    const opened = Date.now();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    const onScroll = (e: Event) => {
      if (Date.now() - opened < 250) return;
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('scroll', onScroll, true); };
  }, [anchor, onClose]);
  return createPortal(
    <div ref={ref} className="vs-sec sc-menu" role="menu" aria-label={label} style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: 0 }}>
      {items.map((it, i) => it === 'sep'
        ? <div key={`s${i}`} className="sep" />
        : (
          <button key={it.label} type="button" role="menuitem" className={it.danger ? 'danger' : ''} disabled={it.disabled} title={it.title}
            onClick={() => { onClose(); it.onClick(); }}>
            {it.icon && <Ic k={it.icon} />}{it.label}
          </button>
        ))}
    </div>,
    document.body,
  );
}

/** useMenu(): { open(anchor, items), el } - one popover at a time, anchored to its trigger. */
export function useMenu(label: string): { el: ReactNode; open: (anchor: HTMLElement, items: MenuItem[]) => void; openFor: HTMLElement | null } {
  const [st, setSt] = useState<{ anchor: HTMLElement; items: MenuItem[] } | null>(null);
  const close = useCallback(() => {
    setSt((cur) => { cur?.anchor.setAttribute('aria-expanded', 'false'); return null; });
  }, []);
  const open = useCallback((anchor: HTMLElement, items: MenuItem[]) => {
    setSt((cur) => {
      if (cur && cur.anchor === anchor) { anchor.setAttribute('aria-expanded', 'false'); return null; }
      cur?.anchor.setAttribute('aria-expanded', 'false');
      anchor.setAttribute('aria-expanded', 'true');
      return { anchor, items };
    });
  }, []);
  const el = st ? <PopMenu anchor={st.anchor} items={st.items} onClose={close} label={label} /> : null;
  return { el, open, openFor: st?.anchor ?? null };
}

/** Stable id helper for label/input pairs. */
export function useFieldId(prefix: string): string { return `${prefix}-${useId().replace(/:/g, '')}`; }

/** Inline error box used inside drawers and modals. */
export function InlineError({ text }: { text: string }) {
  if (!text) return null;
  return <div className="sc-warn bad" role="alert"><Ic k="warn" /><div>{text}</div></div>;
}
